/* eslint-disable react/prop-types */
import { Divider } from "@mui/material";
import { t } from "i18next";
import Collapse from "library/Molecules/Collapse";
import { first, size } from "lodash";
import useNewOrderMachine from "pages/NewOrder/hooks/useNewOrderMachine";
import EstimationDetailsLayout from "pages/NewOrder/layouts/EstimationLayout/EstimationDetailsLayout/EstimationDetailsLayout";
import EstimationRowLayout from "pages/NewOrder/layouts/EstimationLayout/EstimationRowLayout/EstimationRowLayout";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContactPaymentType } from "store/features/contact.store";
import { retrieveContactTimezone } from "store/features/localization.store";
import { formatDateFlat } from "utility/date-fns";
import { formatPrice } from "utility/helpers/general";
import { getPriceWithVat } from "utility/helpers/orders";
import { REPEAT_ORDER_TYPES } from "../../../../../constants";
import EstimatedDetailsLine from "./EstimatedDetailsLine/EstimatedDetailsLine";

const EstimatedDetails = () => {
  const timezone = useSelector(retrieveContactTimezone);
  const { isCheckoutPaymentType } = useSelector(retrieveContactPaymentType);

  const [
    {
      order: { repeat_settings },
      price,
    },
  ] = useNewOrderMachine();

  const priceByDate = useMemo(() => {
    if (repeat_settings.type !== REPEAT_ORDER_TYPES.EXACT_DATES) {
      return [
        {
          date: formatDateFlat(first(price).date, timezone),
          ...getPriceWithVat(first(price), isCheckoutPaymentType),
          startingPrice: first(price).starting_price,
        },
      ];
    }
    return price.map((curr) => ({
      date: formatDateFlat(curr.date, timezone),
      ...getPriceWithVat(curr, isCheckoutPaymentType),
      startingPrice: curr.starting_price,
    }));
  }, [isCheckoutPaymentType, price, repeat_settings.type, timezone]);

  const totalPrice = useMemo(() => {
    if (repeat_settings.type !== REPEAT_ORDER_TYPES.EXACT_DATES) return null;
    return {
      currency: first(price).currency,
      price: price.reduce((prev, curr) => prev + getPriceWithVat(curr, isCheckoutPaymentType).totalPrice, 0),
    };
  }, [isCheckoutPaymentType, price, repeat_settings.type]);

  if (repeat_settings.type !== REPEAT_ORDER_TYPES.EXACT_DATES) {
    return <EstimatedDetailsLine className="mt-4" price={first(priceByDate)} />;
  }
  return (
    <>
      {size(priceByDate) > 2 ? (
        <div className="position-relative">
          <Collapse className="flex-wrap align-items-start" collapsedSize={200}>
            {priceByDate.map((currPrice) => (
              <EstimatedDetailsLine key={currPrice.date} date={currPrice.date} price={{ ...currPrice }} />
            ))}
          </Collapse>
        </div>
      ) : (
        priceByDate.map((currPrice) => (
          <EstimatedDetailsLine key={currPrice.date} date={currPrice.date} price={{ ...currPrice }} />
        ))
      )}

      <Divider className="my-3" />

      <EstimationDetailsLayout
        totalPriceSection={
          <EstimationRowLayout
            titleClassName="text-fw-bold text-underlined text-uppercase"
            titleSection={t("utils.pricing.totalPrice")}
            contentSection={
              <Trans
                i18nKey={isCheckoutPaymentType ? "utils.pricing.inclVAT" : "utils.pricing.exclVAT"}
                tOptions={{ price: formatPrice(totalPrice.price, totalPrice.currency ?? "") }}
                components={{ span: <span /> }}
                defaults={
                  isCheckoutPaymentType ? "<span>{{price}}</span> inc. VAT" : "<span>{{price}}</span> excl. VAT"
                }
              />
            }
          />
        }
        data-cy="estimation-total-line"
      />
    </>
  );
};

export default EstimatedDetails;
